/**
 * BloomLadderDemo — self-playing animation explaining scaffolded quiz
 * generation: one concept climbing Bloom's taxonomy from Remember to Create,
 * with each sample question building on the one before it. Same looping
 * mechanics as the landing page feature demos.
 */
import React, { useEffect, useState } from 'react';

// Fixed Bloom palette (matches BloomsPyramid.tsx)
const LEVELS = [
  {
    name: 'Remember',
    color: '#1A2744',
    question: 'What is the time complexity of binary search?',
    connection: '',
  },
  {
    name: 'Understand',
    color: '#1A5A8A',
    question: 'You recalled it is O(log n). Explain why binary search needs a sorted array to achieve that.',
    connection: 'Builds on the fact recalled in Remember',
  },
  {
    name: 'Apply',
    color: '#1A7A3E',
    question: 'Using that idea, trace binary search on [3, 8, 14, 21, 30] to find 21. Which indexes get checked?',
    connection: 'Puts the Understand explanation to work',
  },
  {
    name: 'Analyze',
    color: '#8B7A0E',
    question: 'Compare your trace with a linear scan of the same list. Where exactly does binary search win, and when would it not?',
    connection: 'Dissects the behavior you just applied',
  },
  {
    name: 'Evaluate',
    color: '#9C3A10',
    question: 'A teammate runs binary search on unsorted log files. Using your analysis, judge the approach and its risks.',
    connection: 'Judges a real case with the Analyze insight',
  },
  {
    name: 'Create',
    color: '#8B1A1A',
    question: 'Design a search strategy for a contacts app that stays fast as data grows. Justify your choices with everything above.',
    connection: 'Combines all five levels into something new',
  },
];

const HOLD_STEPS = 2; // closing beats after level 6

const BloomLadderDemo: React.FC<{ className?: string }> = ({ className = '' }) => {
  const totalSteps = LEVELS.length + HOLD_STEPS;
  const [step, setStep] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setStep((s) => (s + 1) % totalSteps), 2600);
    return () => clearInterval(id);
  }, [totalSteps]);

  const activeIdx = Math.min(step, LEVELS.length - 1);
  const active = LEVELS[activeIdx];
  const closing = step >= LEVELS.length;

  return (
    <div className={`overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-large dark:border-secondary-700 dark:bg-secondary-800 ${className}`}>
      {/* window chrome, like the landing demos */}
      <div className="flex items-center gap-1.5 border-b border-gray-100 bg-gray-50 px-4 py-2 dark:border-secondary-700 dark:bg-secondary-900">
        <span className="h-2.5 w-2.5 rounded-full bg-red-400" />
        <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
        <span className="h-2.5 w-2.5 rounded-full bg-green-400" />
        <span className="ml-3 truncate text-xs font-medium text-gray-500 dark:text-gray-400">
          How we generate quizzes / Bloom's taxonomy
        </span>
      </div>

      <div className="flex gap-4 p-4">
        {/* Bloom ladder */}
        <div className="flex w-32 flex-shrink-0 flex-col-reverse gap-1">
          {LEVELS.map((level, i) => {
            const reached = i <= activeIdx || closing;
            const isActive = i === activeIdx && !closing;
            return (
              <div
                key={level.name}
                className={`flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-[11px] font-bold transition-all duration-500 ${
                  isActive ? 'scale-105 text-white shadow-medium' : reached ? 'text-white opacity-80' : 'text-gray-400 dark:text-gray-500'
                }`}
                style={{
                  backgroundColor: reached ? level.color : 'transparent',
                  border: reached ? 'none' : '1.5px dashed #d1d5db',
                }}
              >
                <span className="w-3 text-center">{i + 1}</span>
                {level.name}
              </div>
            );
          })}
        </div>

        {/* Question pane */}
        <div className="flex min-h-[190px] min-w-0 flex-1 flex-col justify-center gap-2">
          {!closing ? (
            <div key={step} className="demo-pop">
              <span
                className="inline-block rounded-full px-2.5 py-0.5 text-[11px] font-bold text-white"
                style={{ backgroundColor: active.color }}
              >
                Level {activeIdx + 1}: {active.name}
              </span>
              <p className="mt-2 text-sm font-semibold leading-snug text-gray-800 dark:text-gray-100">
                {active.question}
              </p>
              {active.connection && (
                <p className="mt-2 flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-gray-400">
                  <svg className="h-3.5 w-3.5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 19V5M5 12l7-7 7 7" />
                  </svg>
                  {active.connection}
                </p>
              )}
            </div>
          ) : (
            <div key="closing" className="demo-pop text-center">
              <div className="mx-auto flex flex-wrap justify-center gap-1">
                {LEVELS.map((l, i) => (
                  <span key={l.name} className="h-2.5 w-8 rounded-full" style={{ backgroundColor: l.color, opacity: 0.9 - 0 * i }} />
                ))}
              </div>
              <p className="mt-3 text-sm font-bold text-gray-800 dark:text-gray-100">
                One concept, six depths of understanding.
              </p>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Every generated question set climbs this ladder, and each question builds on the one before it.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BloomLadderDemo;
